import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { decodeToken, isTokenExpired } from './jwt';

const AMPLITUDE_API_KEY = 'fc36fcc0028992fc512a8d239b6626ad';

/** User IDs for whom we do not enable session recording (e.g. internal team) */
const SESSION_REPLAY_OPT_OUT_USER_IDS = ['673247877e4ac0a824cbac0d']; // Boukar

/**
 * Product/webapp routes where Session Replay should run at 100%.
 * Landing, blog, and other marketing pages are excluded.
 */
const SESSION_REPLAY_WEBAPP_PREFIXES = [
  '/workspace',
  '/onboarding',
  '/invite-onboarding',
  '/dashboard',
  '/upload',
  '/files',
  '/settings',
  '/content-calendar',
  '/calendar/',
  '/montage',
  '/thumbnail-maker',
  '/password',
  '/admin',
  '/backoffice',
  '/b-roll-ideas',
  '/auth',
  '/getlate',
  '/news',
  '/counter',
  '/dark',
  '/the-button-page',
  '/media-kit',
];

let sessionReplayAdded = false;

function isSessionReplayOptedOut() {
  if (typeof localStorage === 'undefined') return false;
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired(token)) return false;
  const payload = decodeToken(token);
  const userId = payload?.userId ?? payload?.id ?? payload?.sub;
  return Boolean(userId && SESSION_REPLAY_OPT_OUT_USER_IDS.includes(userId));
}

export function isWebappPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  const path = pathname || '/';
  return SESSION_REPLAY_WEBAPP_PREFIXES.some((prefix) => {
    const base = prefix.endsWith('/') ? prefix.slice(0, -1) : prefix;
    return path === base || path.startsWith(`${base}/`);
  });
}

/**
 * Enable Session Replay only on webapp routes (100%). No-op on landing/blog/marketing
 * and for opted-out users. Safe to call on route changes after init.
 */
export function syncSessionReplayForPath(pathname) {
  if (sessionReplayAdded || isSessionReplayOptedOut()) return;
  if (!isWebappPath(pathname)) return;
  amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));
  sessionReplayAdded = true;
}

export const initAmplitude = () => {
  console.log('[Amplitude] Initializing...');

  // Session replay: 100% on webapp only; off on landing + blog (+ other marketing).
  syncSessionReplayForPath(
    typeof window !== 'undefined' ? window.location.pathname : ''
  );

  amplitude.init(AMPLITUDE_API_KEY, {
    serverZone: 'EU',
    autocapture: {
      elementInteractions: true
    },
    logLevel: process.env.NODE_ENV === 'production' ? 1 : 3,  // 0 NONE, 1 ERROR, 2 WARN, 3 INFO, 4 DEBUG
  });

  amplitude.track('app_initialized', {
    timestamp: new Date().toISOString()
  });
};

// Utility function to track events
export const trackEvent = (eventName, eventProperties = {}) => {
  amplitude.track(eventName, eventProperties);
};
